import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { BarChart3, FileText, Users, Calendar, TrendingUp, Loader2 } from "lucide-react";
import { requestApiJson } from "@/lib/api-client";

export const StatisticsDashboard = ({ isOpen, onClose }) => {
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadStatistics();
    }
  }, [isOpen]);

  const loadStatistics = async () => {
    setIsLoading(true);
    try {
      const data = await requestApiJson("/statistics");
      setStats(data);
    } catch (error) {
      console.error("Fehler beim Laden der Statistiken:", error);
    }
    setIsLoading(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="statistics-dashboard-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            Statistiken
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : stats ? (
          <div className="space-y-6">
            {/* Overview cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2 text-blue-600 mb-1">
                  <FileText className="w-4 h-4" />
                  <span className="text-xs font-medium">Gesamt</span>
                </div>
                <div className="text-2xl font-bold text-blue-800">{stats.total_documents}</div>
                <div className="text-xs text-blue-600">Dokumente</div>
              </div>

              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-2 text-green-600 mb-1">
                  <TrendingUp className="w-4 h-4" />
                  <span className="text-xs font-medium">Diesen Monat</span>
                </div>
                <div className="text-2xl font-bold text-green-800">{stats.documents_this_month}</div>
                <div className="text-xs text-green-600">Neue Dokumente</div>
              </div>

              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                <div className="flex items-center gap-2 text-purple-600 mb-1">
                  <Users className="w-4 h-4" />
                  <span className="text-xs font-medium">Prüfer</span>
                </div>
                <div className="text-2xl font-bold text-purple-800">{stats.total_inspectors}</div>
                <div className="text-xs text-purple-600">Registriert</div>
              </div>

              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-center gap-2 text-amber-600 mb-1">
                  <Calendar className="w-4 h-4" />
                  <span className="text-xs font-medium">Termine</span>
                </div>
                <div className="text-2xl font-bold text-amber-800">{stats.upcoming_events}</div>
                <div className="text-xs text-amber-600">Geplant</div>
              </div>
            </div>

            {/* Top objects */}
            {stats.top_objects && stats.top_objects.length > 0 && (
              <div>
                <h3 className="text-sm font-medium mb-3">Häufig geprüfte Objekte</h3>
                <div className="space-y-2">
                  {stats.top_objects.map((obj, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg">
                      <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-xs font-medium">
                        {idx + 1}
                      </div>
                      <div className="flex-1 text-sm truncate">{obj.name || "Ohne Name"}</div>
                      <div className="text-sm font-medium text-muted-foreground">{obj.count}x</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Monthly chart (simple bar representation) */}
            {stats.monthly_data && stats.monthly_data.length > 0 && (
              <div>
                <h3 className="text-sm font-medium mb-3">Dokumente pro Monat</h3>
                <div className="flex items-end gap-2 h-32 bg-slate-50 rounded-lg p-4">
                  {stats.monthly_data.map((month, idx) => {
                    const maxCount = Math.max(...stats.monthly_data.map(m => m.count), 1);
                    const height = (month.count / maxCount) * 100;
                    return (
                      <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                        <div 
                          className="w-full bg-blue-500 rounded-t transition-all"
                          style={{ height: `${Math.max(height, 4)}%` }}
                        />
                        <span className="text-xs text-muted-foreground">{month.month.split(' ')[0]}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            Keine Daten verfügbar
          </div>
        )}

        <div className="flex justify-end mt-4">
          <Button variant="outline" onClick={onClose}>
            Schließen
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StatisticsDashboard;
